import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

import { formatToDealerTime } from 'utils/dateUtils';
import type { NurturingPlanVersion } from '../types';

interface PlanVersionSelectProps {
  versions: NurturingPlanVersion[];
  selectedVersionId: string;
  versionsLoading?: boolean;
  onVersionChange: (id: string) => void;
}

function getVersionDisplayInfo(v: NurturingPlanVersion): {
  versionLabel: string;
  status: string;
  dateStr: string;
} {
  const status = v.nurturing_plan_status;
  const versionLabel =
    v.nurturing_plan_version != null ? `v${v.nurturing_plan_version}` : 'Draft';

  if (status === 'PUBLISHED' && v.published_date) {
    return { versionLabel, status: 'PUBLISHED', dateStr: formatToDealerTime(v.published_date) };
  }
  if (status === 'ARCHIVED' && v.archived_date) {
    return { versionLabel, status: 'ARCHIVED', dateStr: formatToDealerTime(v.archived_date) };
  }
  if (status === 'CREATED' && v.created_at) {
    return { versionLabel, status: 'CREATED', dateStr: formatToDealerTime(v.created_at) };
  }
  return { versionLabel, status, dateStr: '' };
}

const STATUS_CHIP_COLORS: Record<string, 'success' | 'default' | 'info'> = {
  PUBLISHED: 'success',
  ARCHIVED: 'default',
  CREATED: 'info',
};

function VersionOptionContent({
  v,
  compact,
}: {
  v: NurturingPlanVersion;
  compact?: boolean;
}) {
  const { versionLabel, status, dateStr } = getVersionDisplayInfo(v);
  const chipColor = STATUS_CHIP_COLORS[status] ?? 'default';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 0.5 : 1,
        flexWrap: compact ? 'nowrap' : 'wrap',
        py: compact ? 0 : 0.25,
        overflow: compact ? 'hidden' : undefined,
        minWidth: 0,
      }}
    >
      <Typography
        variant="body2"
        fontWeight={600}
        sx={
          compact
            ? { fontSize: '0.75rem', lineHeight: 1.2, whiteSpace: 'nowrap', flexShrink: 0 }
            : undefined
        }
      >
        {versionLabel}
      </Typography>
      <Chip
        label={status}
        color={chipColor}
        size="small"
        sx={{
          height: compact ? 18 : 22,
          fontSize: compact ? '0.65rem' : '0.7rem',
          fontWeight: 600,
          flexShrink: 0,
          '& .MuiChip-label': { px: compact ? 0.75 : undefined },
        }}
      />
      {dateStr && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: compact ? '0.65rem' : '0.7rem',
            lineHeight: 1.2,
            whiteSpace: compact ? 'nowrap' : undefined,
            overflow: compact ? 'hidden' : undefined,
            textOverflow: compact ? 'ellipsis' : undefined,
          }}
        >
          {dateStr}
        </Typography>
      )}
    </Box>
  );
}

export const PlanVersionSelect = ({
  versions,
  selectedVersionId,
  versionsLoading,
  onVersionChange,
}: PlanVersionSelectProps) => {
  const showVersions = versions.length > 0;
  if (!showVersions && !versionsLoading) return null;

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: 280,
        '& .MuiOutlinedInput-root': {
          height: 30.75,
          fontSize: '0.8125rem',
        },
        '& .MuiSelect-select': {
          py: '0 !important',
          minHeight: 'unset !important',
          display: 'flex',
          alignItems: 'center',
        },
      }}
    >
      <InputLabel id="nurturing-plan-version-select-label">Version</InputLabel>
      <Select
        labelId="nurturing-plan-version-select-label"
        label="Version"
        value={selectedVersionId ?? ''}
        onChange={(e) => onVersionChange(e.target.value as string)}
        disabled={versionsLoading}
        endAdornment={
          versionsLoading ? <CircularProgress size={16} sx={{ mr: 2 }} /> : undefined
        }
        renderValue={(value) => {
          const v = versions.find((x) => x._id === value);
          return v ? <VersionOptionContent v={v} compact /> : (value as string);
        }}
      >
        {versions.map((v) => (
          <MenuItem key={v._id} value={v._id}>
            <VersionOptionContent v={v} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
